// UPI-app icon + label resolver, driven by a bundled catalog (src/data/upiApps.json).
// The catalog maps iOS/Android package identifiers -> { displayName, iconUrl } where
// iconUrl points at the branded circle SVG in Firebase Storage. Resolution priority:
//   Icon : catalog iconUrl (Firebase) -> native host base64 icon -> generic "UPI" badge.
//   Label: catalog displayName -> a decent native displayName -> prettified id.
// The catalog is imported STATICALLY and its lookup map built once at app load, so the
// default/preferred UPI app icon resolves on first render (no lazy chunk, no icon flash).
// Each native app object is enriched via enrichUpiApp() before it reaches the UI.

const DEFAULT_UPI = { bg: '#097939', fg: '#ffffff', label: 'UPI', size: 12 };

const badge = ({ bg, fg, label, size }) => {
    const svg =
        `<svg xmlns='http://www.w3.org/2000/svg' width='40' height='40' viewBox='0 0 40 40'>` +
        `<rect width='40' height='40' rx='10' fill='${bg}'/>` +
        `<text x='20' y='21' font-family='Inter, Arial, sans-serif' font-size='${size}' font-weight='700' ` +
        `fill='${fg}' text-anchor='middle' dominant-baseline='central'>${label}</text></svg>`;
    return `data:image/svg+xml,${encodeURIComponent(svg)}`;
};

// Generic green "UPI" badge — the universal fallback (also used as <img> onError target).
export const genericUpiBadge = () => badge(DEFAULT_UPI);

const norm = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]/g, '');

// Build a package/name -> { displayName, iconUrl } lookup from the bundled catalog JSON.
export const buildUpiCatalog = (data) => {
    const base = data?.imageBaseUrl || '';
    const map = new Map();
    (data?.apps || []).forEach((a) => {
        const entry = {
            displayName: a.displayName,
            iconUrl: a.imageName ? `${base}${a.imageName}` : null,
        };
        const keys = [
            a.androidPackage,
            a.iosPackage,
            (a.iosPackage || '').replace('://', ''),
            a.displayName,
        ];
        keys.forEach((k) => {
            if (!k) return;
            map.set(String(k).toLowerCase(), entry);
            map.set(norm(k), entry);
        });
    });
    return map;
};

// Attach { catalogName, iconUrl } onto a native app object using the loaded catalog.
export const enrichUpiApp = (app, catalog) => {
    if (!app) return app;
    if (!catalog) return app;
    const id = app.id || '';
    const entry =
        catalog.get(String(id).toLowerCase()) ||
        catalog.get(String(id).replace('://', '').toLowerCase()) ||
        catalog.get(norm(id)) ||
        catalog.get(norm(app.displayName)) ||
        null;
    if (!entry) return app;
    return { ...app, catalogName: entry.displayName, iconUrl: entry.iconUrl };
};

// Prettify a raw id/name like "GOOGLEPAY" / "amazon_pay" into "Googlepay" / "Amazon Pay".
const prettify = (raw) => {
    if (!raw) return 'UPI';
    return String(raw)
        .replace(/[_-]+/g, ' ')
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .trim()
        .split(/\s+/)
        .map((w) => (w.length <= 3 ? w.toUpperCase() : w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()))
        .join(' ') || 'UPI';
};

// Friendly display label. Catalog name wins; else keep a decent native displayName
// (mixed case or multi-word); else prettify the id.
export const getUpiLabel = (app) => {
    if (app?.catalogName) return app.catalogName;
    const dn = (app?.displayName || '').trim();
    if (dn && (/\s/.test(dn) || dn !== dn.toUpperCase())) return dn;
    return prettify(dn || app?.id);
};

export const getUpiIconSrc = (app) => {
    if (!app) return genericUpiBadge();
    if (app.iconUrl) return app.iconUrl;
    if (app.icon) return app.icon.startsWith('data:') ? app.icon : `data:image/png;base64,${app.icon}`;
    return genericUpiBadge();
};
